import { useFormatMenuItem } from "hooks/useFormatMenuItem";
import {
    Overlay,
    OverlayTitleBarProps,
    PanelMenuItem,
    PlantProtectionRoutes,
    RoleType,
    useRouteSearch,
    useTitle,
} from "modules/common";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { AppRoutes, RouteParams } from "routing";
import { RootState } from "store";
import { FlavescenceDoree } from "./FlavescenceDoree";

const allowedRoles = [
    RoleType.SuperAdmin,
    RoleType.EquipmentAdmin,
    RoleType.CommunicatorAdviser,
    RoleType.MinistryAdviser,
];

const getMenuItems = (userRole?: RoleType): PanelMenuItem[] => [
    ...(userRole !== undefined && allowedRoles.includes(userRole)
        ? [
              {
                  label: "Analiza stanja štetnika",
                  link: PlantProtectionRoutes.PestAnalysis,
              },
              {
                  label: "Kretanje brojnosti populacije štetnika",
                  link: PlantProtectionRoutes.PestPopulationSize,
                  fullScreen: true,
              },
              {
                  label: "Faze rasta i razvoja biljaka",
                  link: PlantProtectionRoutes.Phenophases,
              },
          ]
        : []),
    {
        label: "Karta zlatne žutice",
        link: PlantProtectionRoutes.FlavescenceDoreeMap,
        internalLink: true,
    },
    {
        label: "Bolesti i štetnici",
        link: PlantProtectionRoutes.DiseasesAndPests,
    },
    {
        label: "Preporuke o zaštiti bilja",
        link: PlantProtectionRoutes.Recommendations,
    },
    {
        label: "Sakupljanje ambalažnog otpada",
        link: PlantProtectionRoutes.CollectionOfPackagingWaste,
        fullScreen: true,
    },
];

const STORAGE_KEY = "plantProtection.selectedLink";

export const PlantProtection = () => {
    useTitle("Zaštita bilja - eSavjetnik");

    const { search, pathname } = useLocation();
    const history = useHistory();
    const userRole = useSelector((state: RootState) => state.signIn.user?.role.value);

    const menuItems: PanelMenuItem[] = useMemo(() => getMenuItems(userRole), [userRole]);
    const [menuItem, setMenuItem] = useState(menuItems.find(mi => !mi.isDisabled));
    const [parameters, setParameters] = useState<{ recommendationId?: string }>({});

    useEffect(() => {
        const params = new URLSearchParams(search);
        const recommendationId = params.get(RouteParams.RecommendationId);

        if (recommendationId) {
            setParameters({ recommendationId });
        } else {
            setParameters({});
        }
    }, [search]);

    useEffect(() => {
        if (menuItems.length === 0) return;

        const routeMap: Record<string, string> = {
            [PlantProtectionRoutes.PestAnalysis]: PlantProtectionRoutes.PestAnalysis,
            [PlantProtectionRoutes.PestPopulationSize]: PlantProtectionRoutes.PestPopulationSize,
            [PlantProtectionRoutes.Phenophases]: PlantProtectionRoutes.Phenophases,
            [PlantProtectionRoutes.FlavescenceDoreeMap]: PlantProtectionRoutes.FlavescenceDoreeMap,
            [PlantProtectionRoutes.DiseasesAndPests]: PlantProtectionRoutes.DiseasesAndPests,
            [PlantProtectionRoutes.Recommendations]: PlantProtectionRoutes.Recommendations,
            [PlantProtectionRoutes.CollectionOfPackagingWaste]:
                PlantProtectionRoutes.CollectionOfPackagingWaste,
        };

        const targetRoute = routeMap[pathname];

        if (targetRoute) {
            const foundMenuItem = menuItems.find(mi => mi.link === targetRoute);
            if (foundMenuItem) {
                setMenuItem(foundMenuItem);
                sessionStorage.setItem(STORAGE_KEY, foundMenuItem.link);
            }
        } else if (pathname === AppRoutes.PlantProtection) {
            const params = new URLSearchParams(search);
            const recommendationId = params.get(RouteParams.RecommendationId);

            if (recommendationId) {
                const rec = menuItems.find(mi => mi.link === PlantProtectionRoutes.Recommendations);
                if (rec) {
                    setMenuItem(rec);
                    sessionStorage.setItem(STORAGE_KEY, rec.link);
                    history.replace(`${rec.link}${search}`);
                    return;
                }
            }

            const defaultMenuItem =
                menuItems.find(mi => mi.link === PlantProtectionRoutes.PestAnalysis) ??
                menuItems.find(mi => !mi.isDisabled) ??
                menuItems[0];
            if (defaultMenuItem) {
                setMenuItem(defaultMenuItem);
                sessionStorage.setItem(STORAGE_KEY, defaultMenuItem.link);
                history.push(defaultMenuItem.link);
            }
        }
    }, [pathname, menuItems, history, search]);

    useEffect(() => {
        if (parameters.recommendationId) {
            const rec = menuItems.find(mi => mi.link === PlantProtectionRoutes.Recommendations);
            if (rec && rec.link !== menuItem?.link) {
                setMenuItem(rec);
                sessionStorage.setItem(STORAGE_KEY, rec.link);
            }
        }
    }, [parameters, menuItems, menuItem?.link]);

    useEffect(() => {
        return () => {
            sessionStorage.removeItem(STORAGE_KEY);
        };
    }, []);

    const { searchElement } = useRouteSearch({
        routes: [PlantProtectionRoutes.DiseasesAndPests, PlantProtectionRoutes.Recommendations],
        menuItem,
        inputProps: {
            placeholder: "Pretraži po naslovu, sadržaju i kategoriji.",
        },
    });

    const titleBarProps = useMemo<OverlayTitleBarProps | undefined>(
        () =>
            menuItem?.link.includes("karta-zlatne-zutice")
                ? {
                      aboveMenu: true,
                      text: "Karte demarkiranih područja u 2024. godini",
                  }
                : undefined,
        [menuItem?.link]
    );

    const fullScreenButton = useMemo<boolean>(() => {
        return menuItem?.link.includes("faze-rasta-i-razvoja-biljaka") ||
            menuItem?.link.includes("analiza-stanja-stetnika")
            ? true
            : false;
    }, [menuItem?.link]);

    const formatMenuItem = useFormatMenuItem(menuItem?.label);

    return (
        <Overlay
            containerClass={`${formatMenuItem} plant_protection`}
            key={`${pathname}-${menuItem?.link}`}
            fullScreenButton={fullScreenButton}
            backButton
            titleBarProps={titleBarProps}
            search={searchElement}
            fullScreenInitially={menuItem?.fullScreen ?? false}
            view={{
                title: "Zaštita bilja",
                source: mi => `${mi?.link ?? ""}${search}`,
                internalLinkComponents: {
                    [PlantProtectionRoutes.FlavescenceDoreeMap]: state => (
                        <FlavescenceDoree setShowLoading={state.setShowLoading} />
                    ),
                },
                parameters: parameters,
                dynamicHeight: true,
            }}
            menuItems={menuItems}
            selectedMenuItem={menuItem}
            onMenuItemChanged={mi => {
                history.push(mi.link);
            }}
        />
    );
};
